import type { ReactNode } from 'react';
import { Box, Stack, Typography } from '@locus-taxy/disco-ui';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { getTestId } from '@locus-taxy/encore-utils';
import { TestIdAction } from '../utils/testIdUtils';
import { useProfilePage } from './UserDetails/useProfilePage';
import { ProfileHeaderCard } from './UserDetails/ProfileHeaderCard';
import { PersonalInfoSection } from './UserDetails/PersonalInfoSection';
import { LocaleSettingsSection } from './UserDetails/LocaleSettingsSection';
import { WorkProfileSection } from './UserDetails/WorkProfileSection';
import { SessionAccessSection } from './UserDetails/SessionAccessSection';
import { UnsavedChangesDialog } from './UserDetails/UnsavedChangesDialog';
import { ProfileTitleActions } from './UserDetails/ProfileTitleActions';

export default function UserDetails() {
  const { t } = useTranslation();
  const {
    mode,
    isDirty,
    isSaving,
    showUnsavedDialog,
    canUpdate,
    clientId,
    fullName,
    emailAddress,
    formInitialData,
    isLoading,
    error,
    profile,
    editRestrictedReason,
    saveNeedsApproval,
    canEditPhoto,
    configuredTimezone,
    configuredLocale,
    storedTimezone,
    storedLocale,
    timezoneOptions,
    localeOptions,
    genderOptions,
    lastUpdated,
    photoUrl,
    photoLoading,
    hasPhoto,
    workProfileData,
    hasWorkProfile,
    personalInfoViewFields,
    localeViewFields,
    personalInfoFormRef,
    localeFormRef,
    handleEditClick,
    handleCancel,
    handleSave,
    handleDialogStay,
    handleDialogLeave,
    handlePersonalInfoChange,
    handleLocaleChange,
    handlePhotoSelect,
    handlePhotoRemove,
    handleLogout,
  } = useProfilePage();

  let pageBody: ReactNode;
  if (isLoading) {
    pageBody = (
      <Typography
        variant="body2"
        color="muted"
        data-testid={getTestId({
          action: TestIdAction.VIEW,
          object: 'profile-loading',
          page: 'account',
        })}
      >
        {t('dashboard.userDetails.loadingProfile', 'Loading your profile…')}
      </Typography>
    );
  } else if (error && !profile) {
    pageBody = (
      /* The page this replaces degraded silently — blank fields, dead button, no message.
         The read raises no toast on purpose, so the failure has to be said here.
         Only when nothing is loaded: React Query keeps the cached profile when a background
         refetch fails, and replacing the page then would unmount the edit forms mid-edit. */
      <Typography
        variant="body2"
        color="destructive"
        data-testid={getTestId({
          action: TestIdAction.VIEW,
          object: 'profile-error',
          page: 'account',
        })}
      >
        {t(
          'dashboard.userDetails.profileLoadFailed',
          'Your profile could not be loaded. Please refresh, or contact your administrator if this continues.'
        )}
      </Typography>
    );
  } else {
    pageBody = (
      <Stack direction="column" gap={3}>
        <ProfileHeaderCard
          mode={mode}
          fullName={fullName}
          designation={emailAddress || undefined}
          roleName={workProfileData.roleName || undefined}
          photoUrl={photoUrl}
          photoLoading={photoLoading}
          onPhotoSelect={handlePhotoSelect}
          onPhotoRemove={handlePhotoRemove}
          hasPhoto={hasPhoto}
          canEditPhoto={canEditPhoto}
        />
        <PersonalInfoSection
          mode={mode}
          formInitialData={formInitialData}
          clientId={clientId}
          formRef={personalInfoFormRef}
          onChange={handlePersonalInfoChange}
          viewFields={personalInfoViewFields}
          genderOptions={genderOptions}
        />
        <LocaleSettingsSection
          mode={mode}
          clientId={clientId}
          formRef={localeFormRef}
          onChange={handleLocaleChange}
          timezone={configuredTimezone}
          timezoneOptions={timezoneOptions}
          locale={configuredLocale}
          localeOptions={localeOptions}
          storedTimezone={storedTimezone}
          storedLocale={storedLocale}
          viewFields={localeViewFields}
        />
        {/* No person entity backs a cross-organisation caller: the block is absent, not empty. */}
        {hasWorkProfile && (
          <WorkProfileSection
            roleName={workProfileData.roleName}
            accessDimensionLabel={workProfileData.accessDimensionLabel}
            accessDimensionNames={workProfileData.accessDimensionNames}
            skillNames={workProfileData.skillNames}
            joiningDate={workProfileData.joiningDate}
            locale={configuredLocale}
          />
        )}
      </Stack>
    );
  }
  return (
    <Stack
      direction="column"
      className="h-full overflow-hidden"
      data-testid={getTestId({
        action: TestIdAction.VIEW,
        object: 'account-page-container',
        page: 'account',
      })}
    >
      {/* TitleBar */}
      <Stack
        direction="row"
        justify="between"
        align="center"
        className="shrink-0 bg-card px-12 py-6"
        data-testid={getTestId({
          action: TestIdAction.VIEW,
          object: 'profile-title-bar',
          page: 'account',
        })}
      >
        <Stack direction="column" gap={1}>
          <Typography variant="h4" fontWeight="bold">
            {t('dashboard.userDetails.profileSettings', 'Profile Settings')}
          </Typography>
          {lastUpdated && (
            <Typography variant="caption" color="muted">
              {t(
                'dashboard.userDetails.lastUpdated',
                'Last updated: {{date}}',
                {
                  date: lastUpdated,
                }
              )}
            </Typography>
          )}
        </Stack>

        <ProfileTitleActions
          mode={mode}
          canUpdate={canUpdate}
          editRestrictedReason={editRestrictedReason}
          isSaving={isSaving}
          isDirty={isDirty}
          saveNeedsApproval={saveNeedsApproval}
          onEdit={handleEditClick}
          onCancel={handleCancel}
          onSave={handleSave}
        />
      </Stack>

      {/* Scrollable content */}
      <Box className="flex-1 overflow-auto bg-muted/30 px-0 pt-8 pb-12">
        <Box className="mx-auto w-full max-w-3xl px-4">
          <Stack direction="column" gap={3}>
            {pageBody}
            {/* Outside the load states: Logout is not part of the profile, and master always
                showed it, so a failed or pending read must not take it away. */}
            <SessionAccessSection onLogout={handleLogout} />
          </Stack>
        </Box>
      </Box>

      <UnsavedChangesDialog
        open={showUnsavedDialog}
        onStay={handleDialogStay}
        onLeave={handleDialogLeave}
      />
    </Stack>
  );
}
